"use strict";

/**
  * @reach/dialog v0.18.0
  *
  * Copyright (c) 2018-2022, React Training LLC
  *
  * This source code is licensed under the MIT license found in the
  * LICENSE.md file in the root directory of this source tree.
  *
  * @license MIT
  */

// src/reach-dialog.tsx
import _defineProperty from "/Users/timhorton/Programming/repos/chalice/chalice-home/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/Users/timhorton/Programming/repos/chalice/chalice-home/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _slicedToArray from "/Users/timhorton/Programming/repos/chalice/chalice-home/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
var _excluded = ["isOpen", "children"],
  _excluded2 = ["allowPinchZoom", "as", "dangerouslyBypassFocusLock", "dangerouslyBypassScrollLock", "initialFocusRef", "onClick", "onDismiss", "onKeyDown", "onMouseDown", "unstable_lockFocusAcrossFrames"],
  _excluded3 = ["as", "isOpen"],
  _excluded4 = ["as", "onClick", "onKeyDown"],
  _excluded5 = ["allowPinchZoom", "initialFocusRef", "isOpen", "onDismiss"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import * as React from "react";
import { Portal } from "@reach/portal";
import { composeEventHandlers, createContext, getOwnerDocument, noop, useComposedRefs } from "@reach/utils";
import FocusLock from "react-focus-lock";
import { RemoveScroll } from "react-remove-scroll";
var _createContext = createContext("DialogContext", {
    isOpen: false
  }),
  _createContext2 = _slicedToArray(_createContext, 2),
  DialogContextProvider = _createContext2[0],
  useDialogContext = _createContext2[1];
function DialogWrapper(_ref) {
  var _ref$isOpen = _ref.isOpen,
    isOpen = _ref$isOpen === void 0 ? true : _ref$isOpen,
    children = _ref.children,
    props = _objectWithoutProperties(_ref, _excluded);
  React.useEffect(function () {
    if (isOpen) {
      window.__REACH_DISABLE_TOOLTIPS = true;
    } else {
      window.requestAnimationFrame(function () {
        window.__REACH_DISABLE_TOOLTIPS = false;
      });
    }
  }, [isOpen]);
  return /* @__PURE__ */React.createElement(Portal, _objectSpread({
    "data-reach-dialog-wrapper": "",
    "data-state": isOpen ? "open" /* Open */ : "closed" /* Closed */
  }, props), /* @__PURE__ */React.createElement(DialogContextProvider, {
    isOpen: isOpen
  }, children));
}
DialogWrapper.displayName = "unstable_DialogWrapper";
var DialogInner = React.forwardRef(function DialogInner2(_ref2, forwardedRef) {
  var allowPinchZoom = _ref2.allowPinchZoom,
    _ref2$as = _ref2.as,
    Comp = _ref2$as === void 0 ? "div" : _ref2$as,
    dangerouslyBypassFocusLock = _ref2.dangerouslyBypassFocusLock,
    dangerouslyBypassScrollLock = _ref2.dangerouslyBypassScrollLock,
    initialFocusRef = _ref2.initialFocusRef,
    onClick = _ref2.onClick,
    _ref2$onDismiss = _ref2.onDismiss,
    onDismiss = _ref2$onDismiss === void 0 ? noop : _ref2$onDismiss,
    onKeyDown = _ref2.onKeyDown,
    onMouseDown = _ref2.onMouseDown,
    unstable_lockFocusAcrossFrames = _ref2.unstable_lockFocusAcrossFrames,
    props = _objectWithoutProperties(_ref2, _excluded2);
  var _useDialogContext = useDialogContext("DialogInner"),
    isOpen = _useDialogContext.isOpen;
  var lockFocusAcrossFramesIsDefined = unstable_lockFocusAcrossFrames !== void 0;
  if (true) {
    React.useEffect(function () {
      if (lockFocusAcrossFramesIsDefined) {
        console.warn("The unstable_lockFocusAcrossFrames in @reach/dialog is deprecated. It will be removed in the next minor release.");
      }
    }, [lockFocusAcrossFramesIsDefined]);
  }
  var mouseDownTarget = React.useRef(null);
  var overlayNode = React.useRef(null);
  var ref = useComposedRefs(overlayNode, forwardedRef);
  var activateFocusLock = React.useCallback(function () {
    if (initialFocusRef && initialFocusRef.current) {
      initialFocusRef.current.focus();
    }
  }, [initialFocusRef]);
  function handleClick(event) {
    if (mouseDownTarget.current === event.target) {
      event.stopPropagation();
      onDismiss(event);
    }
  }
  function handleKeyDown(event) {
    if (event.key === "Escape") {
      event.stopPropagation();
      onDismiss(event);
    }
  }
  function handleMouseDown(event) {
    mouseDownTarget.current = event.target;
  }
  React.useEffect(function () {
    return overlayNode.current ? createAriaHider(overlayNode.current) : void 0;
  }, []);
  return /* @__PURE__ */React.createElement(FocusLock, {
    autoFocus: true,
    returnFocus: true,
    onActivation: activateFocusLock,
    disabled: dangerouslyBypassFocusLock != null ? dangerouslyBypassFocusLock : !isOpen,
    crossFrame: unstable_lockFocusAcrossFrames !== null && unstable_lockFocusAcrossFrames !== void 0 ? unstable_lockFocusAcrossFrames : true
  }, /* @__PURE__ */React.createElement(RemoveScroll, {
    allowPinchZoom: allowPinchZoom,
    enabled: dangerouslyBypassScrollLock != null ? !dangerouslyBypassScrollLock : isOpen
  }, /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({}, props), {}, {
    ref: ref,
    "data-reach-dialog-inner": "",
    "data-state": isOpen ? "open" /* Open */ : "closed" /* Closed */,
    onClick: composeEventHandlers(onClick, handleClick),
    onKeyDown: composeEventHandlers(onKeyDown, handleKeyDown),
    onMouseDown: composeEventHandlers(onMouseDown, handleMouseDown)
  }))));
});
DialogInner.displayName = "DialogInner";
var DialogOverlay = React.forwardRef(function DialogOverlay2(_ref3, forwardedRef) {
  var _ref3$as = _ref3.as,
    Comp = _ref3$as === void 0 ? "div" : _ref3$as,
    _ref3$isOpen = _ref3.isOpen,
    isOpen = _ref3$isOpen === void 0 ? true : _ref3$isOpen,
    props = _objectWithoutProperties(_ref3, _excluded3);
  return isOpen ? /* @__PURE__ */React.createElement(DialogWrapper, {
    isOpen: isOpen
  }, /* @__PURE__ */React.createElement(DialogInner, _objectSpread({
    "data-reach-dialog-overlay": "",
    ref: forwardedRef,
    as: Comp
  }, props))) : null;
});
DialogOverlay.displayName = "DialogOverlay";
var DialogContent = React.forwardRef(function DialogContent2(_ref4, forwardedRef) {
  var _ref4$as = _ref4.as,
    Comp = _ref4$as === void 0 ? "div" : _ref4$as,
    onClick = _ref4.onClick,
    onKeyDown = _ref4.onKeyDown,
    props = _objectWithoutProperties(_ref4, _excluded4);
  var _useDialogContext2 = useDialogContext("DialogContent"),
    isOpen = _useDialogContext2.isOpen;
  return /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({
    "aria-modal": "true",
    role: "dialog",
    tabIndex: -1
  }, props), {}, {
    ref: forwardedRef,
    "data-reach-dialog-content": "",
    "data-state": isOpen ? "open" /* Open */ : "closed" /* Closed */,
    onClick: composeEventHandlers(onClick, function (event) {
      event.stopPropagation();
    })
  }));
});
DialogContent.displayName = "DialogContent";
var Dialog = React.forwardRef(function Dialog2(_ref5, forwardedRef) {
  var _ref5$allowPinchZoom = _ref5.allowPinchZoom,
    allowPinchZoom = _ref5$allowPinchZoom === void 0 ? false : _ref5$allowPinchZoom,
    initialFocusRef = _ref5.initialFocusRef,
    isOpen = _ref5.isOpen,
    _ref5$onDismiss = _ref5.onDismiss,
    onDismiss = _ref5$onDismiss === void 0 ? noop : _ref5$onDismiss,
    props = _objectWithoutProperties(_ref5, _excluded5);
  return /* @__PURE__ */React.createElement(DialogOverlay, {
    allowPinchZoom: allowPinchZoom,
    initialFocusRef: initialFocusRef,
    isOpen: isOpen,
    onDismiss: onDismiss
  }, /* @__PURE__ */React.createElement(DialogContent, _objectSpread({
    ref: forwardedRef
  }, props)));
});
Dialog.displayName = "Dialog";
function createAriaHider(dialogNode) {
  var originalValues = [];
  var rootNodes = [];
  var ownerDocument = getOwnerDocument(dialogNode);
  if (!dialogNode) {
    if (true) {
      console.warn("A ref has not yet been attached to a dialog node when attempting to call `createAriaHider`.");
    }
    return noop;
  }
  Array.prototype.forEach.call(ownerDocument.querySelectorAll("body > *"), function (node) {
    var _dialogNode$parentNod, _dialogNode$parentNod2;
    var portalNode = (_dialogNode$parentNod = dialogNode.parentNode) === null || _dialogNode$parentNod === void 0 ? void 0 : (_dialogNode$parentNod2 = _dialogNode$parentNod.parentNode) === null || _dialogNode$parentNod2 === void 0 ? void 0 : _dialogNode$parentNod2.parentNode;
    if (node === portalNode) {
      return;
    }
    var attr = node.getAttribute("aria-hidden");
    var alreadyHidden = attr !== null && attr !== "false";
    if (alreadyHidden) {
      return;
    }
    originalValues.push(attr);
    rootNodes.push(node);
    node.setAttribute("aria-hidden", "true");
  });
  return function () {
    rootNodes.forEach(function (node, index) {
      var originalValue = originalValues[index];
      if (originalValue === null) {
        node.removeAttribute("aria-hidden");
      } else {
        node.setAttribute("aria-hidden", originalValue);
      }
    });
  };
}
export { Dialog, DialogContent, DialogInner, DialogOverlay, DialogWrapper as unstable_DialogWrapper };